import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Menu, X, LogOut, User, MessageSquare } from 'lucide-react';
import { APP_CONFIG, NAV_ITEMS } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { toast } from '@/components/ui/sonner';
import type { HeaderProps } from '@/types';

/**
 * Header Component
 * Main navigation header with responsive mobile menu
 */
export function Header({ isMenuOpen, onMenuToggle, className }: HeaderProps) {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Track authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      console.log('Auth state changed:', currentUser?.email || 'No user');
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const handleNavClick = (href: string) => {
    // Close mobile menu when navigation item is clicked
    if (isMenuOpen) {
      onMenuToggle();
    }

    // Handle different types of navigation
    if (href.startsWith('#')) {
      // Check if we're on the home page
      const currentPath = window.location.pathname;

      if (currentPath === '/' || currentPath === '') {
        // We're on home page, smooth scroll to section
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // We're on a different page, navigate to home with anchor
        navigate(`/${href}`);
      }
    } else if (href.startsWith('/')) {
      // Navigate to different page
      navigate(href);
    }
  };

  const handleLoginClick = () => {
    navigate('/login');
    if (isMenuOpen) {
      onMenuToggle();
    }
  };

  const handleGetStartedClick = () => {
    navigate('/signup');
    if (isMenuOpen) {
      onMenuToggle();
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Logged out successfully!');
      navigate('/');
      if (isMenuOpen) {
        onMenuToggle();
      }
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Error logging out. Please try again.');
    }
  };

  return (
    <header className={cn(
      "border-b border-border/10 backdrop-blur-sm bg-background/80 sticky top-0 z-50",
      className
    )}>
      <div className="container mx-auto px-4 py-4">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <img
              src="/images/logo.png"
              alt="NeurosEdge Logo"
              className="h-10 w-auto object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.href}
                onClick={() => handleNavClick(item.href)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="flex items-center space-x-4">
            {loading ? (
              // Loading state
              <div className="w-6 h-6 border-2 border-gray-600 border-t-primary rounded-full animate-spin hidden md:block"></div>
            ) : user ? (
              // User is logged in
              <div className="hidden md:flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <User className="w-4 h-4" />
                  <span className="text-sm">{user.email}</span>
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate('/support')}
                  className="border-blue-600 text-blue-400 hover:bg-blue-600 hover:text-white"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  My Support
                </Button>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            ) : (
              // User is not logged in
              <>
                <Button
                  variant="ghost"
                  className="hidden md:inline-flex"
                  onClick={handleLoginClick}
                >
                  Login
                </Button>
                <Button
                  className="bg-primary hover:bg-primary/90 shadow-glow hidden md:inline-flex"
                  onClick={handleGetStartedClick}
                >
                  Sign Up
                </Button>
              </>
            )}
            
            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={onMenuToggle}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-border/10">
            <div className="flex flex-col space-y-4 pt-4">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.href}
                  onClick={() => handleNavClick(item.href)}
                  className="text-muted-foreground hover:text-foreground transition-colors py-2 text-left"
                >
                  {item.label}
                </button>
              ))}
              
              <div className="flex flex-col space-y-3 pt-2">
                {loading ? (
                  // Loading state
                  <div className="w-6 h-6 border-2 border-gray-600 border-t-primary rounded-full animate-spin"></div>
                ) : user ? (
                  // User is logged in
                  <>
                    <div className="flex items-center space-x-2 text-muted-foreground py-2">
                      <User className="w-4 h-4" />
                      <span className="text-sm">{user.email}</span>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        navigate('/support');
                        if (isMenuOpen) onMenuToggle();
                      }}
                      className="justify-start border-blue-600 text-blue-400 hover:bg-blue-600 hover:text-white"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      My Support
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleLogout}
                      className="justify-start border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </Button>
                  </>
                ) : (
                  // User is not logged in
                  <>
                    <Button
                      variant="ghost"
                      onClick={handleLoginClick}
                      className="justify-start"
                    >
                      Login
                    </Button>
                    <Button
                      className="bg-primary hover:bg-primary/90 shadow-glow justify-start"
                      onClick={handleGetStartedClick}
                    >
                      Sign Up
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
